/**
 * The `daemons` block of a hostMetrics sample (WI 34278): expected-vs-running
 * launchd / scheduled-task units, published by the Control Center.
 */
import type { HostDaemons } from './host-metric.models';

type Obj = Record<string, unknown>;
const pick = (o: Obj, camel: string, pascal: string): unknown =>
  o[camel] ?? o[pascal];

/** Wire (camel or PascalCase) -> HostDaemons, or null when absent or malformed. */
export function normalizeHostDaemons(raw: unknown): HostDaemons | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Obj;
  const missingRaw = pick(r, 'missing', 'Missing');
  const missing: HostDaemons['missing'] = [];
  for (const m of Array.isArray(missingRaw) ? missingRaw : []) {
    if (!m || typeof m !== 'object') continue;
    const label = pick(m as Obj, 'label', 'Label');
    if (typeof label !== 'string' || !label) continue;
    const reason = pick(m as Obj, 'reason', 'Reason');
    const lastExit = pick(m as Obj, 'lastExit', 'LastExit');
    missing.push({
      label,
      reason: typeof reason === 'string' ? reason : null,
      lastExit: typeof lastExit === 'number' ? lastExit : null,
    });
  }
  const checkedAt = pick(r, 'checkedAt', 'CheckedAt');
  return {
    expected: Number(pick(r, 'expected', 'Expected') ?? 0) || 0,
    checkedAt: typeof checkedAt === 'string' ? checkedAt : null,
    missing,
  };
}

/**
 * A sample without a daemons block keeps the host's previous report: a second
 * publisher on the same host (legacy agent, Python CC) must not blank it.
 * When both carry one, the newer checkedAt wins whatever order the samples
 * arrived in: the CC reports once a minute and routinely lands behind the
 * agent's heartbeat. Without a comparable checkedAt, a late sample keeps the
 * held report and a fresh one replaces it.
 */
export function carryDaemons(
  prev: HostDaemons | null | undefined,
  next: HostDaemons | null,
  nextIsLate = false
): HostDaemons | null {
  if (!next) return prev ?? null;
  if (!prev) return next;
  const held = Date.parse(prev.checkedAt ?? '');
  const incoming = Date.parse(next.checkedAt ?? '');
  if (Number.isNaN(held) || Number.isNaN(incoming)) {
    return nextIsLate ? prev : next;
  }
  return incoming >= held ? next : prev;
}
